import React from 'react';

// The single card every viewer-facing state uses: password, single sign-on,
// unavailable, and lost connection. Those four used to look like four different
// products — a glass card, plain capitalised white text, and two raw strings —
// because each was written where it was needed. One shell now, so the only thing
// that changes between them is the icon, the words, and the action.
//
// Deliberately restrained, and monochrome. It fronts games, architecture
// walkthroughs and government training sims alike, directly beneath whatever
// logo the customer uploaded — so it carries no colour of its own and lets
// theirs be the only one on screen. No decorative motion either.
//
// Self-contained by necessity — the player embeds in customer pages, so every
// rule is namespaced `spg-` and class-based (a host stylesheet targeting
// `input`, `button` or `h2` must not reach in, and nothing here may leak out),
// and properties a host could set — font, box-sizing, line-height — are stated
// rather than inherited.
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export const CSS = `
/* MONOCHROME ON PURPOSE. This card sits under whatever logo the customer
   uploaded, so any colour of ours competes with theirs — a mint primary button
   under a purple wordmark reads as two brands arguing. With the card in
   neutrals, the only colour on screen is the customer's own logo, which is what
   makes one screen work for a game studio, an architecture practice and a
   government department alike. Red on the error block is the single exception:
   that is semantic, not decorative. */
.spg-root{position:absolute;inset:0;z-index:2;
  display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;
  overflow-y:auto;font-family:${FONT};}

/* Only drawn when the project has a loading banner: a scrim sized to the card,
   so their artwork still reads at the edges rather than being dimmed frame-wide. */
.spg-scrim{position:absolute;inset:0;pointer-events:none;
  background:radial-gradient(ellipse 40% 48% at 50% 50%,rgba(0,0,0,.66) 0%,rgba(0,0,0,.34) 50%,rgba(0,0,0,0) 78%);}

.spg-card{position:relative;width:100%;max-width:360px;box-sizing:border-box;
  padding:30px 28px;border-radius:12px;text-align:center;color:#fff;
  background:rgba(19,20,22,.94);
  -webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);
  border:1px solid rgba(255,255,255,.08);
  box-shadow:0 12px 40px rgba(0,0,0,.4);
  animation:spg-in .28s ease-out both;}
@keyframes spg-in{from{opacity:0;transform:translate3d(0,6px,0)}to{opacity:1;transform:none}}
/* A banner can be bright; hold the card's edge against one. */
.spg-branded .spg-card{border-color:rgba(255,255,255,.14);box-shadow:0 18px 50px rgba(0,0,0,.58)}

/* No chip, no tinted background — the icon is a quiet label for the state, not
   an ornament. */
.spg-icon{display:flex;align-items:center;justify-content:center;
  margin:0 auto 12px;color:rgba(255,255,255,.4);}

.spg-title{margin:0 0 6px;font-size:17px;font-weight:600;letter-spacing:-.01em;
  line-height:1.35;color:#f2f3f4;}
.spg-sub{margin:0;font-size:13px;line-height:1.55;color:rgba(255,255,255,.5);}

.spg-form{display:flex;flex-direction:column;gap:9px;margin-top:20px;width:100%;}
.spg-field{position:relative;display:flex;align-items:center;}
.spg-input{width:100%;box-sizing:border-box;padding:11px 40px 11px 13px;font-family:${FONT};
  font-size:14.5px;line-height:1.2;color:#fff;border-radius:8px;
  background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.12);
  outline:none;transition:border-color .15s,box-shadow .15s;-webkit-appearance:none;}
.spg-input::placeholder{color:rgba(255,255,255,.3)}
.spg-input:disabled{opacity:.5;cursor:not-allowed}
.spg-input:focus{border-color:rgba(255,255,255,.3);box-shadow:0 0 0 3px rgba(255,255,255,.07)}
.spg-reveal{position:absolute;right:4px;display:flex;align-items:center;justify-content:center;
  width:30px;height:30px;padding:0;border:0;border-radius:6px;background:transparent;cursor:pointer;
  color:rgba(255,255,255,.38);transition:color .15s,background .15s;}
.spg-reveal:hover{color:rgba(255,255,255,.85);background:rgba(255,255,255,.06)}
.spg-reveal:focus-visible{outline:1px solid rgba(255,255,255,.5);outline-offset:1px}

/* White on dark: reads as system UI rather than as anyone's brand colour. */
.spg-btn{width:100%;box-sizing:border-box;padding:11px 16px;font-family:${FONT};
  font-size:14.5px;font-weight:600;line-height:1.2;border:0;border-radius:8px;cursor:pointer;
  color:#121315;background:#f3f4f5;transition:background .15s,opacity .15s;}
.spg-btn:hover:not(:disabled){background:#fff}
.spg-btn:focus-visible{outline:2px solid rgba(255,255,255,.6);outline-offset:2px}
.spg-btn:disabled{opacity:.35;cursor:not-allowed}
.spg-btn-row{display:flex;align-items:center;justify-content:center;gap:8px}

.spg-err{display:flex;align-items:flex-start;gap:8px;margin:0;padding:9px 11px;text-align:left;
  font-size:12.5px;line-height:1.45;color:#f2b8b8;border-radius:7px;
  background:rgba(255,90,90,.08);border:1px solid rgba(255,90,90,.2);}

/* Diagnostic detail (a close code, a reason) — available for support without
   being the first thing a viewer reads. */
.spg-detail{margin:14px 0 0;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;
  font-size:10px;letter-spacing:.04em;color:rgba(255,255,255,.26);line-height:1.4;
  word-break:break-word;}

@media (max-width:420px){
  .spg-root{padding:16px}
  .spg-card{padding:26px 20px}
  .spg-title{font-size:16px}
}
@media (prefers-reduced-motion:reduce){ .spg-card{animation:none} }
`;

export const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

export const IconShield = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l7.5 3v5.6c0 4.4-3.1 7.9-7.5 9.4-4.4-1.5-7.5-5-7.5-9.4V6z" />
    <path d="M9 12.2l2.1 2.1 4-4.2" />
  </svg>
);

export const IconEye = ({ off }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {off ? (
      <>
        <path d="M9.9 5.1A9.7 9.7 0 0 1 12 4.9c5 0 9 4.6 9 7.1 0 1-.7 2.3-1.8 3.5M6.3 6.9C3.9 8.4 3 10.8 3 12c0 2.5 4 7.1 9 7.1 1.4 0 2.7-.35 3.8-.9" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
      </>
    ) : (
      <>
        <path d="M3 12c0-2.5 4-7.1 9-7.1s9 4.6 9 7.1-4 7.1-9 7.1S3 14.5 3 12z" />
        <circle cx="12" cy="12" r="2.9" />
      </>
    )}
  </svg>
);

// Defaults to the inline size used beside error text; `size` is passed when it
// stands alone as the card's icon.
export const IconAlert = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={size > 16 ? 1.7 : 2} strokeLinecap="round"
    style={size > 16 ? undefined : { flexShrink: 0, marginTop: '1px' }} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.2M12 16.3v.1" />
  </svg>
);

export const IconUnplug = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17l-3 3M20 4l-3 3" />
    <path d="M9.5 7.5l7 7-2.4 2.4a3.4 3.4 0 0 1-4.8 0l-2.2-2.2a3.4 3.4 0 0 1 0-4.8z" />
  </svg>
);

/**
 * The card shell. `banner` switches on the scrim treatment for projects that
 * supply loading artwork; callers that already paint the backdrop themselves
 * pass `bare` so this does not draw a second one.
 */
const StatusCard = ({
  banner = false,
  icon,
  title,
  subtitle,
  detail,
  children,
  role = 'dialog',
  label,
}) => {
  // No logo here on purpose. App.js pins the customer's logo where the project
  // asked for it, across every state, so a second centred copy inside the card
  // put the same mark on screen twice.
  return (
    <div className={banner ? 'spg-root spg-branded' : 'spg-root'}>
      {banner && <div className="spg-scrim" aria-hidden="true" />}
      <div
        className="spg-card"
        role={role}
        {...(role === 'dialog' ? { 'aria-modal': 'true' } : {})}
        aria-label={label}
      >
        {icon && <div className="spg-icon">{icon}</div>}
        {title && <h2 className="spg-title">{title}</h2>}
        {subtitle && <p className="spg-sub">{subtitle}</p>}
        {children}
        {detail && <p className="spg-detail">{detail}</p>}
      </div>
    </div>
  );
};

// The customer's logo, pinned where the project asked for it.
//
// Each full-screen surface renders its OWN copy — the overlay, the disconnect
// screen, and the live view — so none depends on a sibling elsewhere in the tree
// surviving a state change. A single hoisted copy was tried first and went
// missing on the terminal-disconnect path; the element now lives inside the
// thing it belongs to. Where two surfaces overlap the upper one's opaque
// backdrop hides the lower, so only one is ever visible.
//
// It is NEVER placed inside the card: the logo belongs in the corner the project
// chose, not centred in a dialog.
const LOGO_OFFSETS = {
  TopLeft: { top: '12px', left: '12px' },
  TopRight: { top: '12px', right: '12px' },
  BottomLeft: { bottom: '12px', left: '12px' },
  BottomRight: { bottom: '12px', right: '12px' },
};

export const BrandLogo = ({ branding = {} }) => {
  const logo = branding.customLogo;
  if (!logo || logo === 'NOLOGO') return null;
  // Corner default when the project chose no position. Not merged under a
  // chosen one: a default `top` beneath BottomLeft would pin both edges.
  const offsets = LOGO_OFFSETS[branding.logoPosition] || { top: '12px', left: '12px' };
  return (
    <img
      src={logo}
      alt=""
      style={{ position: 'absolute', zIndex: 5, maxWidth: '150px', maxHeight: '48px',
               objectFit: 'contain', pointerEvents: 'none', ...offsets }}
    />
  );
};

export default StatusCard;
